import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { supportedVendorsSchema } from '@eluve/graphql-types';

const c = initContract();

const TenantSessionSchema = z.object({});

const AuthAppTypeSchema = z.enum(['mobile', 'web', 'review']);
export type AuthAppType = z.infer<typeof AuthAppTypeSchema>;

const TryInitiateLoginResultSchema = z.union([
  z.object({
    type: z.literal('saml'),
    redirectUrl: z.string().url(),
  }),
  z.object({
    type: z.literal('social'),
    thirdPartyId: z.string(),
  }),
  z.object({
    type: z.literal('email'),
    isExistingUser: z.boolean(),
  }),
]);

export type TryInitiateLoginResult = z.infer<
  typeof TryInitiateLoginResultSchema
>;

const ImpersonateUserSchema = z.object({
  targetUserId: z.string().uuid(),
  tenantId: z.string().uuid(),
  reason: z.string(),
  originUrl: z.string().url().optional(),
});

const ImpersonationResponseSchema = z.object({
  message: z.string(),
  expiresAt: z.string().datetime().optional(),
});

export const authContract = c.router(
  {
    tryInitiateLogin: {
      method: 'POST',
      path: '/initiate-login',
      responses: {
        200: TryInitiateLoginResultSchema,
      },
      body: z.object({
        appType: AuthAppTypeSchema,
        email: z.string().email(),
      }),
    },
    createTenantSession: {
      method: 'POST',
      path: '/switch-tenant',
      responses: {
        201: TenantSessionSchema,
        403: z.object({
          message: z.string(),
        }),
      },
      body: z.object({
        tenantId: z.string().optional(),
      }),
      summary:
        'Attempts to create a session for a user scoped to a specified tenant',
    },
    getTenantToken: {
      method: 'POST',
      path: '/tenant-token',
      responses: {
        201: z.object({
          jwt: z.string(),
        }),
        403: z.object({
          message: z.string(),
        }),
      },
      body: z.object({
        tenantId: z.string(),
      }),
      summary:
        'Mints a short-lived tenant-scoped JWT for the requesting tab without mutating the shared session (ENG-5545)',
    },
    refreshAllowedTenants: {
      method: 'POST',
      path: '/refresh-allowed-tenants',
      responses: {
        201: TenantSessionSchema,
        403: z.object({
          message: z.string(),
        }),
      },
      body: z.object({}),
      summary:
        'Refreshes the list of tenants that the user has access to and returns a new session',
    },
    attemptPartnerLogin: {
      method: 'POST',
      path: '/attempt-partner-login',
      responses: {
        200: z.object({
          redirectUrl: z.string().url().optional(),
        }),
      },
      body: z.object({
        appType: AuthAppTypeSchema,
        email: z.string().email(),
      }),
      summary:
        'Attempts to sign in with a white listed partner using an enterprise SSO flow',
    },
    sso: {
      method: 'POST',
      path: '/sso',
      responses: {
        201: TenantSessionSchema,
      },
      body: z.object({
        code: z.string(),
        state: z.string(),
      }),
    },
    /**
     * Using API key, generate a magic link for a user to login
     */
    generateMagicLink: {
      method: 'POST',
      path: '/tenants/:tenantId/magic-link',
      body: z.object({
        vendor: supportedVendorsSchema,
        user: z.unknown(),
      }),
      headers: z.object({
        authorization: z.string(),
      }),
      responses: {
        201: z.object({
          magicLink: z.string(),
        }),
        403: z.object({
          message: z.string(),
        }),
      },
    },
    impersonateUser: {
      method: 'POST',
      path: '/impersonate',
      responses: {
        200: ImpersonationResponseSchema,
        403: z.object({
          message: z.string(),
        }),
      },
      body: ImpersonateUserSchema,
      summary: 'Start impersonating a user (eluve-admin only)',
    },
    exitImpersonation: {
      method: 'POST',
      path: '/exit-impersonation',
      responses: {
        200: ImpersonationResponseSchema,
        400: z.object({
          message: z.string(),
        }),
      },
      body: z.object({}),
      summary: 'Exit impersonation and return to admin session',
    },
    renewImpersonation: {
      method: 'POST',
      path: '/renew-impersonation',
      responses: {
        200: ImpersonationResponseSchema,
        400: z.object({
          message: z.string(),
        }),
      },
      body: z.object({}),
      summary: 'Renew current impersonation session',
    },
    getFeatureFlags: {
      method: 'GET',
      path: '/feature-flags',
      query: z.object({
        // Cache discriminator only: the authenticated session is the
        // identity. Kept in the URL so the browser HTTP cache key naturally
        // varies per user (lets us drop `Vary: Cookie`).
        u: z.string().optional(),
        // The tenant to EVALUATE flags for. Membership-verified server-side
        // (403 for non-members); falls back to the session claim when absent
        // (old bundles). Also serves as the per-tenant cache discriminator.
        // [ENG-5545]
        t: z.string().optional(),
      }),
      responses: {
        200: z.object({
          featureFlags: z.record(z.union([z.string(), z.boolean()])),
          featureFlagPayloads: z.record(z.unknown()).optional(),
        }),
        403: z.object({
          message: z.string(),
        }),
        503: z.object({
          message: z.string(),
        }),
      },
      summary: 'Evaluates PostHog feature flags for the authenticated user',
    },
  },
  {
    pathPrefix: '/auths',
  },
);
